if (!customElements.get('product-card-slider')) {
  const DEFAULT_MEDIA_QUERY = '(max-width: 749px)';

  class ProductCardSlider extends HTMLElement {
    connectedCallback() {
      this.mediaQuery = window.matchMedia(this.dataset.mediaQuery || DEFAULT_MEDIA_QUERY);
      this.handleMediaChange = () => this.evaluate();
      this.mediaQuery.addEventListener('change', this.handleMediaChange);
      this.evaluate();
    }

    disconnectedCallback() {
      if (this.mediaQuery) {
        this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      }
      this.deactivate();
    }

    evaluate() {
      if (this.mediaQuery.matches) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    activate() {
      if (this.active) return;
      if (this.closest('.swiper')) return;

      this.image =
        this.querySelector('[data-slider-image]') ||
        this.querySelector('.image-container img.primary') ||
        this.querySelector('.image-container img');
      this.link = this.querySelector('a.image-container') || this.querySelector('a');
      this.bar = this.querySelector('[data-slider-bar]');
      this.track = this.querySelector('[data-slider-track]');
      this.marker = this.querySelector('[data-slider-marker]');

      const dataEl = this.querySelector('[data-slider-angles]');
      if (!this.image || !this.link || !this.bar || !this.track || !this.marker || !dataEl) return;

      if (this.image.parentElement && this.image.parentElement.tagName === 'PICTURE') {
        if (this.image.parentElement.querySelector('source')) return;
      }

      try {
        this.angles = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(this.angles) || this.angles.length < 2) return;

      this.original = {
        src: this.image.getAttribute('src'),
        srcset: this.image.getAttribute('srcset'),
      };

      this.index = 0;
      this.dragged = false;
      this.preloaded = false;
      this.stepThreshold = parseInt(this.dataset.stepThreshold, 10) || 30;
      this.invertDrag = this.dataset.invertDrag === 'true';

      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.image.setAttribute('draggable', 'false');
      this.classList.add('card-slider--ready');
      this.active = true;

      const preload = () => this.preload();
      ['pointerenter', 'pointerdown', 'focusin'].forEach((type) => {
        this.addEventListener(type, preload, { once: true, signal });
      });

      this.bindBar(signal);
      this.bindImage(signal);
      this.bindKeyboard(signal);

      this.link.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      this.addEventListener(
        'click',
        (event) => {
          if (!this.dragged) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal }
      );
    }

    deactivate() {
      if (!this.active) return;
      this.active = false;

      if (this.controller) this.controller.abort();
      this.classList.remove('card-slider--ready');

      this.index = 0;
      this.restoreOriginal();

      if (this.marker) {
        this.marker.style.left = '0%';
      }
      if (this.track) {
        this.track.setAttribute('aria-valuenow', '1');
        this.track.setAttribute('aria-valuetext', `1 / ${this.angles.length}`);
      }
    }

    restoreOriginal() {
      if (!this.image || !this.original) return;
      if (this.original.srcset) {
        this.image.srcset = this.original.srcset;
      } else {
        this.image.removeAttribute('srcset');
      }
      if (this.original.src && this.image.getAttribute('src') !== this.original.src) {
        this.image.src = this.original.src;
      }
    }

    setIndex(next) {
      const max = this.angles.length - 1;
      const clamped = Math.min(max, Math.max(0, next));
      if (clamped === this.index) return;
      this.index = clamped;
      this.render();
    }

    setIndexFromX(clientX) {
      const rect = this.track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      this.setIndex(Math.min(this.angles.length - 1, Math.floor(ratio * this.angles.length)));
    }

    render() {
      const angle = this.angles[this.index];
      if (!angle) return;

      if (this.index === 0) {
        this.restoreOriginal();
      } else {
        if (angle.srcset) {
          this.image.srcset = angle.srcset;
        } else {
          this.image.removeAttribute('srcset');
        }
        if (this.image.getAttribute('src') !== angle.src) this.image.src = angle.src;
      }

      const step = 100 / this.angles.length;
      this.marker.style.left = `${this.index * step}%`;

      this.track.setAttribute('aria-valuenow', String(this.index + 1));
      this.track.setAttribute('aria-valuetext', `${this.index + 1} / ${this.angles.length}`);
    }

    preload() {
      if (this.preloaded) return;
      this.preloaded = true;

      const run = () => {
        this.angles.forEach((angle, i) => {
          if (i === 0) return;
          const img = new Image();
          if (angle.srcset) img.srcset = angle.srcset;
          if (this.image.sizes) img.sizes = this.image.sizes;
          img.src = angle.src;
        });
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 600 });
      } else {
        setTimeout(run, 0);
      }
    }

    bindBar(signal) {
      let scrubbing = false;

      this.bar.addEventListener(
        'pointerdown',
        (event) => {
          scrubbing = true;
          this.dragged = true;
          try {
            this.bar.setPointerCapture(event.pointerId);
          } catch (error) {
            /* noop */
          }
          this.setIndexFromX(event.clientX);
          event.preventDefault();
        },
        { signal }
      );

      this.bar.addEventListener(
        'pointermove',
        (event) => {
          if (!scrubbing) return;
          this.setIndexFromX(event.clientX);
          event.preventDefault();
        },
        { signal }
      );

      const end = () => {
        if (!scrubbing) return;
        scrubbing = false;
        this.releaseDrag();
      };

      this.bar.addEventListener('pointerup', end, { signal });
      this.bar.addEventListener('pointercancel', end, { signal });
    }

    bindImage(signal) {
      let dragging = false;
      let stepped = false;
      let moved = false;
      let startX = 0;

      this.link.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (event.target.closest('[data-slider-bar], .swatch, .button-next')) return;
          dragging = true;
          stepped = false;
          moved = false;
          startX = event.clientX;
          try {
            this.link.setPointerCapture(event.pointerId);
          } catch (error) {
            /* noop */
          }
        },
        { signal }
      );

      this.link.addEventListener(
        'pointermove',
        (event) => {
          if (!dragging) return;
          const dx = event.clientX - startX;

          if (!moved && Math.abs(dx) > 4) {
            moved = true;
            this.dragged = true;
          }
          if (!moved || stepped) return;

          if (Math.abs(dx) >= this.stepThreshold) {
            const forward = this.invertDrag ? dx > 0 : dx < 0;
            this.setIndex(this.index + (forward ? 1 : -1));
            stepped = true;
          }
          event.preventDefault();
        },
        { signal }
      );

      const end = () => {
        if (!dragging) return;
        dragging = false;
        stepped = false;
        this.releaseDrag();
      };

      this.link.addEventListener('pointerup', end, { signal });
      this.link.addEventListener('pointercancel', end, { signal });
    }

    bindKeyboard(signal) {
      this.track.addEventListener(
        'keydown',
        (event) => {
          const actions = {
            ArrowLeft: () => this.setIndex(this.index - 1),
            ArrowRight: () => this.setIndex(this.index + 1),
            Home: () => this.setIndex(0),
            End: () => this.setIndex(this.angles.length - 1),
          };
          const action = actions[event.key];
          if (!action) return;
          action();
          event.preventDefault();
        },
        { signal }
      );
    }

    releaseDrag() {
      setTimeout(() => {
        this.dragged = false;
      }, 60);
    }
  }

  customElements.define('product-card-slider', ProductCardSlider);
}
